import React from "react";
import { Link } from "react-router-dom";
import { Sparkles, PhoneCall, Calendar, ArrowUpRight, Compass, CheckCircle2, Flame, Heart, Coins, Zap, Crown } from "lucide-react";

export default function Offerings() {
  const categories = [
    {
      title: "Start Here (Foundational Access)",
      desc: "High-impact introductory experiences to understand what has been holding you back and receive personalized clarity on your next step.",
      items: [
        {
          name: "Emotional Freedom Masterclass",
          price: "Free",
          badge: "Free Registration",
          desc: "Register now and take the first step toward understanding what has been holding you back. Have you ever wondered why some people seem to move forward in life while others keep repeating the same struggles?",
          features: [
            "Understand recurring emotional patterns",
            "Discover what keeps you stuck",
            "A grounded first step into the ESHA journey"
          ],
          ctaText: "Register Free Now",
          ctaUrl: "https://learn.eshawitheswari.com/l/96a7c7eae0",
          icon: Sparkles,
          type: "checkout"
        },
        {
          name: "Private Clarity Call",
          price: "INR 1,199",
          badge: "Limited Spots",
          desc: "One conversation can change your life direction. Whether you are facing relationship challenges, financial stress, low self-confidence, emotional overwhelm, health concerns, or simply feel lost about your next step — this call is designed to help you gain clarity and move forward with confidence.",
          features: [
            "Private clarity conversation",
            "Identify your core life block",
            "A confident path forward"
          ],
          ctaText: "Book Your Clarity Call",
          ctaUrl: "https://learn.eshawitheswari.com/web/lite/events/6a48df41599e3232f7dee506",
          icon: PhoneCall,
          type: "checkout"
        }
      ]
    },
    {
      title: "Signature Transformation Codes",
      desc: "Deep, structured programs that take you from emotional survival to emotional freedom, wealth confidence, self-confidence, and lasting love.",
      items: [
        {
          name: "ESHA Emotional Freedom Blueprint",
          price: "INR 9,999",
          desc: "A foundational emotional intelligence and personal transformation program designed to help you move from emotional survival to emotional freedom. Understand your mind, regulate your emotions, identify hidden patterns, and respond to life with greater confidence and consciousness.",
          features: [
            "Step-by-step emotional freedom journey",
            "Nervous-system awareness & self-reflection",
            "Release emotional blocks that no longer serve you"
          ],
          ctaText: "Join the Blueprint",
          ctaUrl: "https://learn.eshawitheswari.com/l/895b8911c0",
          icon: Compass,
          type: "checkout"
        },
        {
          name: "Emotional Freedom Code",
          price: "INR 50,000 + GST",
          desc: "Heal your past. Transform your present. Create your future. Release what has been holding you back, break emotional patterns, heal deeply and create a lasting inner freedom.",
          features: [
            "Deep emotional pattern-breaking",
            "Heal past wounds at the root",
            "Create lasting inner freedom"
          ],
          ctaText: "Unlock the Code",
          ctaUrl: "https://learn.eshawitheswari.com/l/4ba6aabc08",
          icon: Flame,
          type: "checkout"
        },
        {
          name: "The Wealth Code",
          price: "INR 50,000 + GST",
          desc: "Transform your relationship with money and create wealth with confidence. Money is more than currency — it is a relationship.",
          features: [
            "Rewire your money relationship",
            "Release scarcity conditioning",
            "Build wealth with confidence"
          ],
          ctaText: "Unlock the Wealth Code",
          ctaUrl: "https://learn.eshawitheswari.com/l/936b3a48dc",
          icon: Coins,
          type: "checkout"
        },
        {
          name: "Unstoppable Code",
          price: "INR 50,000 + GST",
          desc: "Become the most confident version of yourself. Confidence is not something you are born with — it is something you build.",
          features: [
            "Build unshakable self-confidence",
            "Dissolve self-doubt patterns",
            "Step into your unstoppable self"
          ],
          ctaText: "Become Unstoppable",
          ctaUrl: "https://learn.eshawitheswari.com/l/3b3e21aa78",
          icon: Zap,
          type: "checkout"
        },
        {
          name: "Sacred Love Code",
          price: "INR 50,000 + GST",
          desc: "Heal yourself. Strengthen your relationship. Create lasting love. Every relationship reflects the emotional patterns, beliefs, and experiences we carry within ourselves — long-lasting transformation begins by understanding and transforming the person in the mirror.",
          features: [
            "Guided relationship transformation journey",
            "Heal yourself first",
            "Create lasting, conscious love"
          ],
          ctaText: "Join Sacred Love Code",
          ctaUrl: "https://learn.eshawitheswari.com/l/4fc5185492",
          icon: Heart,
          type: "checkout"
        },
        {
          name: "ESHA Life Mastery",
          price: "INR 2,00,000 + GST",
          badge: "Flagship Journey",
          desc: "Design your life. Lead yourself. Create your legacy. You have worked on different parts of your life — now it is time to bring them together. A transformational journey uniting clarity, emotional freedom, meaningful relationships, abundance, health, purpose, leadership, and impact, so you move beyond managing life and start living with intention, alignment, and purpose.",
          features: [
            "Integrate every key area of your life",
            "Live with intention, alignment & purpose",
            "Become the creator of your legacy"
          ],
          ctaText: "Begin Life Mastery",
          ctaUrl: "https://learn.eshawitheswari.com/l/2493fee422",
          icon: Crown,
          type: "checkout"
        }
      ]
    },
    {
      title: "Live Experiences & Communities",
      desc: "Weekly live spaces to sustain emotional regulation, community support, and continuous growth.",
      items: [
        {
          name: "ESHA Transformational Saturday Workshop",
          price: "INR 499 / Session",
          badge: "Live Every Saturday",
          desc: "A new experience every Saturday. A new perspective for a better life. A live transformational experience exploring relevant life challenges across Self, Relationships, Wealth & Health — designed to create awareness, emotional clarity, practical shifts, and meaningful personal growth. Registration covers the next upcoming Saturday session only; register separately for any additional Saturday.",
          features: [
            "Every Saturday, a new theme, one powerful experience",
            "Live transformational group experience",
            "Take one Saturday for yourself — leave with a meaningful shift"
          ],
          ctaText: "Register for This Saturday",
          ctaUrl: "https://learn.eshawitheswari.com/l/0f7dcf71de",
          icon: Calendar,
          type: "checkout"
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen pt-28 pb-20 bg-[#050505] text-[#F6F4EE] relative" data-testid="offerings-page">
      {/* Background soft spotlights */}
      <div className="absolute inset-0 glow-radial opacity-20 pointer-events-none" />
      <div className="absolute bottom-40 left-10 h-96 w-96 glow-emerald opacity-15 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
        {/* Intro */}
        <div className="text-center max-w-3xl mx-auto mb-20">
          <span className="font-mono text-xs tracking-[0.3em] uppercase text-[#00D2FF] px-3 py-1.5 border border-[#00D2FF]/20 rounded-full bg-[#111111]/80 font-bold">
            Official Storefront
          </span>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-[#F6F4EE] leading-tight mt-6 mb-6">
            The ESHA <span className="text-blue-shimmer italic">Offerings Hub</span>
          </h1>
          <p className="font-sans text-base text-[#AAA29E] leading-relaxed">
            All offerings and digital programs published on the official ESHA learning platform. From foundational access points to core mastery tracks, choose your transformational step below.
          </p>
        </div>

        {/* Categories loop */}
        <div className="space-y-24">
          {categories.map((cat, catIdx) => (
            <div key={catIdx} className="border-t border-[#FF8C00]/15 pt-12">
              <div className="max-w-3xl mb-12">
                <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-[#F6F4EE] font-light mb-4">
                  {cat.title}
                </h2>
                <p className="font-sans text-sm sm:text-base text-[#AAA29E] leading-relaxed">
                  {cat.desc}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {cat.items.map((item, itemIdx) => (
                  <div
                    key={itemIdx}
                    className="bg-[#0F0F0F]/80 border border-[#FF8C00]/15 rounded-2xl p-8 flex flex-col justify-between hover:border-[#00D2FF]/45 hover:scale-[1.01] transition-all duration-300 shadow-lg relative group"
                    data-testid={`offering-card-${catIdx}-${itemIdx}`}
                  >
                    <div>
                      {/* Badge / Price */}
                      <div className="flex justify-between items-center mb-6">
                        <span className="font-mono text-xs font-bold text-[#00D2FF]" data-testid={`offering-price-${catIdx}-${itemIdx}`}>
                          {item.price}
                        </span>
                        {item.badge ? (
                          <span className="font-mono text-[9px] uppercase tracking-wider bg-[#FF8C00]/10 text-[#FF8C00] px-2.5 py-0.5 rounded-full font-bold">
                            {item.badge}
                          </span>
                        ) : (
                          <item.icon className="h-4 w-4 text-[#FF8C00]/40" />
                        )}
                      </div>

                      {/* Name */}
                      <h3 className="font-serif text-xl sm:text-2xl text-[#F6F4EE] font-normal leading-snug mb-3">
                        {item.name}
                      </h3>

                      {/* Desc */}
                      <p className="font-sans text-xs sm:text-sm text-[#AAA29E] leading-relaxed mb-6">
                        {item.desc}
                      </p>

                      {/* Features */}
                      <div className="space-y-2 mb-8">
                        {item.features.map((feat, fIdx) => (
                          <div key={fIdx} className="flex gap-2 items-start text-xs text-[#AAA29E]">
                            <CheckCircle2 className="h-3.5 w-3.5 text-[#00D2FF] shrink-0 mt-0.5" />
                            <span>{feat}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* CTA Button */}
                    <div className="border-t border-[#FF8C00]/10 pt-5 mt-4">
                      {item.type === "checkout" ? (
                        <a
                          href={item.ctaUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-full py-3 bg-gradient-to-r from-[#FF8C00] to-[#FF4500] hover:from-[#FFA500] text-white text-xs uppercase tracking-widest font-mono font-bold rounded-xl flex items-center justify-center gap-1.5 transition-all duration-300"
                          data-testid={`offering-cta-${catIdx}-${itemIdx}`}
                        >
                          {item.ctaText}
                          <ArrowUpRight className="h-4 w-4" />
                        </a>
                      ) : (
                        <Link
                          to={item.ctaUrl}
                          className="w-full py-3 border border-[#00D2FF]/30 hover:border-[#00D2FF]/60 text-[#00D2FF] hover:bg-[#00D2FF]/10 text-xs uppercase tracking-widest font-mono font-bold rounded-xl flex items-center justify-center gap-1.5 transition-all duration-300"
                          data-testid={`offering-cta-${catIdx}-${itemIdx}`}
                        >
                          {item.ctaText}
                          <ArrowUpRight className="h-4 w-4" />
                        </Link>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
